import React, { useState, useEffect } from 'react';
import { Globe, Layout, Image, Type, Phone, MessageSquare, Wrench, HelpCircle, ChevronRight, Upload, Eye, Save } from 'lucide-react';
import { useCompany } from '../../lib/hooks';
import { saveSiteConfig, loadSiteConfig, publishSite, getDeployedSite, generatePreviewUrl } from '../../lib/siteMaker';
import ServicesEditor from '../settings/ServicesEditor';
import AboutEditor from '../settings/AboutEditor';
import type { SiteConfig } from '../../types';

const SiteMaker = () => {
  const { company } = useCompany();
  const [activeSection, setActiveSection] = useState<string>('header');
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState<boolean>(false);
  const [siteUrl, setSiteUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [siteData, setSiteData] = useState<SiteConfig>({
    header: {
      logo: company?.logo || '',
      title: company?.name || '',
      tagline: 'Professional Appliance Repair Services',
      phone: company?.phone || '',
      email: company?.email || '',
    },
    hero: {
      image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&q=80',
      headline: 'Expert Appliance Repair',
      subheadline: 'Fast, Reliable Service When You Need It Most',
    },
    services: {
      categories: company?.services?.map(service => ({
        name: service,
        description: `Professional ${service.toLowerCase()} services`,
        icon: 'Wrench',
        items: []
      })) || [],
      featured: company?.services?.slice(0, 3).map(service => ({
        name: service,
        description: `Expert ${service.toLowerCase()} service`,
        icon: 'Wrench'
      })) || []
    },
    reviews: {
      enabled: true,
      featured: [
        {
          author: 'John D.',
          rating: 5,
          text: 'Excellent service! Fixed my refrigerator quickly and professionally.'
        },
        {
          author: 'Sarah M.',
          rating: 5,
          text: 'Very knowledgeable and courteous. Would highly recommend!'
        }
      ]
    },
    about: {
      image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&q=80',
      title: 'About Us',
      content: `With years of experience in appliance repair, ${company?.name} is your trusted partner for all your appliance service needs.`
    },
    faqs: [
      {
        question: 'What areas do you service?',
        answer: 'We service the greater metropolitan area and surrounding suburbs.'
      },
      {
        question: 'Do you offer emergency repairs?',
        answer: 'Yes, we offer emergency repair services for urgent situations.'
      }
    ]
  });

  useEffect(() => {
    async function loadConfig() {
      try {
        if (!company) return;

        const deployment = await getDeployedSite();
        if (deployment) {
          setSiteUrl(deployment.url);
          setSiteData(deployment.config);
        }

        const config = await loadSiteConfig();
        if (config) {
          setSiteData(prev => ({ ...prev, ...config }));
          setPreviewUrl(generatePreviewUrl(config));
        }
      } catch (err) {
        console.error('Error loading site configuration:', err);
        setError('Failed to load site configuration');
      }
    }

    if (company?.id) {
      loadConfig();
    }
  }, [company]);

  const sections = [
    { id: 'header', label: 'Header', icon: Layout },
    { id: 'hero', label: 'Hero Section', icon: Image },
    { id: 'services', label: 'Services', icon: Wrench },
    { id: 'reviews', label: 'Reviews', icon: MessageSquare },
    { id: 'about', label: 'About Us', icon: Type },
    { id: 'faqs', label: 'FAQs', icon: HelpCircle }
  ];

  const showMessage = (message: string, duration = 3000) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), duration);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      await saveSiteConfig(siteData);
      setPreviewUrl(generatePreviewUrl(siteData));
      showMessage('Site configuration saved successfully!');
    } catch (err) {
      console.error('Error saving site configuration:', err);
      setError('Failed to save site configuration');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    setError(null);
    
    try {
      const deployment = await publishSite(siteData);
      setSiteUrl(deployment.url);
      
      showMessage(`Site published successfully! Your site will be live at: ${deployment.url}`, 5000);
    } catch (err) {
      console.error('Error publishing site:', err);
      setError('Failed to publish site');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Site Builder</h1>
                <p className="text-gray-400">Customize your business website</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              {previewUrl && (
                <button
                  onClick={() => window.open(previewUrl, '_blank')}
                  className="flex items-center px-4 py-2 text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Preview
                </button>
              )}

              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-4 py-2 text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700"
              >
                <Save className="w-5 h-5 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </button>

              <button
                onClick={handlePublish}
                disabled={publishing}
                className="flex items-center px-4 py-2 text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700"
              >
                <Upload className="w-5 h-5 mr-2" />
                {publishing ? 'Publishing...' : 'Publish'}
              </button>

              {siteUrl && (
                <a
                  href={siteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-4 py-2 text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700"
                >
                  <Globe className="w-5 h-5 mr-2" />
                  View Live Site
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar */}
          <div className="col-span-3">
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden">
              <div className="p-4 border-b border-gray-700">
                <h2 className="font-medium text-gray-200">Website Sections</h2>
              </div>
              <nav className="space-y-1 p-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeSection === section.id
                        ? 'bg-blue-600/20 text-blue-400'
                        : 'text-gray-400 hover:bg-gray-700/50 hover:text-gray-200'
                    }`}
                  >
                    <section.icon className="w-5 h-5 mr-3" />
                    {section.label}
                    <ChevronRight className={`w-5 h-5 ml-auto ${
                      activeSection === section.id ? 'text-blue-400' : 'text-gray-500'
                    }`} />
                  </button>
                ))}
              </nav>
            </div>

            {/* Preview */}
            <div className="mt-6 bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="font-medium text-gray-200 mb-4">Website Preview</h3>
              <div className="aspect-[3/4] bg-gray-900 rounded-lg overflow-hidden">
                {previewUrl ? (
                  <iframe
                    src={previewUrl}
                    className="w-full h-full border-0"
                    title="Website Preview"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                    Save changes to preview
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Editor */}
          <div className="col-span-9">
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700">
              <div className="p-6 border-b border-gray-700">
                <h2 className="text-lg font-medium text-gray-200">
                  {sections.find(s => s.id === activeSection)?.label} Settings
                </h2>
              </div>
              <div className="p-6">
                {/* Section Editors */}
                {activeSection === 'header' && (
                  <HeaderEditor data={siteData} onChange={setSiteData} />
                )}
                {activeSection === 'hero' && (
                  <HeroEditor data={siteData} onChange={setSiteData} />
                )}
                {activeSection === 'services' && (
                  <ServicesEditor data={siteData} onChange={setSiteData} />
                )}
                {activeSection === 'about' && (
                  <AboutEditor data={siteData} onChange={setSiteData} />
                )}
                {/* Add other section editors */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg">
          {error}
        </div>
      )}
    </div>
  );
};

const HeaderEditor = ({ data, onChange }) => {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Business Name
        </label>
        <input
          type="text"
          value={data.header.title}
          onChange={(e) => onChange({
            ...data,
            header: { ...data.header, title: e.target.value }
          })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tagline
        </label>
        <input
          type="text"
          value={data.header.tagline}
          onChange={(e) => onChange({
            ...data,
            header: { ...data.header, tagline: e.target.value }
          })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number
          </label>
          <input
            type="tel"
            value={data.header.phone}
            onChange={(e) => onChange({
              ...data,
              header: { ...data.header, phone: e.target.value }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            value={data.header.email}
            onChange={(e) => onChange({
              ...data,
              header: { ...data.header, email: e.target.value }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
    </div>
  );
};

const HeroEditor = ({ data, onChange }) => {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Hero Image
        </label>
        <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg bg-gray-50">
          <div className="space-y-1 text-center">
            <div className="mx-auto h-48 w-48 mb-4">
              <img
                src={data.hero.image}
                alt="Hero preview"
                className="h-full w-full object-cover rounded-lg"
              />
            </div>
            <div className="flex text-sm text-gray-600">
              <label className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                <span>Upload a file</span>
                <input type="file" className="sr-only" />
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              PNG, JPG, GIF up to 10MB
            </p>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Headline
        </label>
        <input
          type="text"
          value={data.hero.headline}
          onChange={(e) => onChange({
            ...data,
            hero: { ...data.hero, headline: e.target.value }
          })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Subheadline
        </label>
        <input
          type="text"
          value={data.hero.subheadline}
          onChange={(e) => onChange({
            ...data,
            hero: { ...data.hero, subheadline: e.target.value }
          })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );
};

export default SiteMaker;